/**
 * VE-26943 — the "Unassigned Media" pseudo-folder, expressed as a folder.
 *
 * Unassigned media is the pre-folders Redact data model: one redact-state SDO
 * per TDO (`config.tdoRedactFileStatusRegistryId`), whose EXISTENCE is what marks a
 * file as unassigned — filing a TDO into a case deletes its SDO
 * (`fileTdoWithCaseSuccess`), and uploading with no `parentFolderId` creates one
 * (`onActionCreateTDOSuccessHandle`). Membership therefore cannot be derived
 * from the folder tree, which is why this list is fetched from
 * `structuredDataObjects` rather than a folder's `childTDOs`.
 *
 * That is a data-source difference, not a reason for the whole UI to fork. This
 * module adapts the SDO list into the shape the rest of the folder view already
 * speaks — a `FolderStateType` whose `caseFiles.inputFiles` are `AssetFileType`
 * — so filters, sort, view modes, multi-select, downloads and staleness work on
 * unassigned files without a parallel code path. Each mapped file keeps its
 * `sdoId`, which doubles as the "this row is unassigned" test at the few places
 * that genuinely need to know (delete → SDO delete, move → fileTdo + SDO
 * delete, drag source tagging). Those callers gate on `isUnassignedFile` rather
 * than poking at `sdoId` directly, the same way folder-level callers gate on
 * `isUnassignedFolderId`.
 *
 * The folder is synthetic: its id is a sentinel, not an aiWARE folder id, so it
 * must never be passed to a folder mutation or permission lookup. Callers gate
 * on `isUnassignedFolderId`.
 */
import { CaseId } from '@common-modules/universal/models/Brands';
import { IN_REDACTION_TAG_KEY } from '@common-modules/mediaDetails';
import {
  AssetFileType,
  FolderStateType,
  LegacyMediaDetailType,
  UnassignedTdoRecord,
} from './store';

/**
 * Sentinel folder id for Unassigned Media. Deliberately not a numeric/uuid-ish
 * string so it can never collide with a real aiWARE folder id, and so a stray
 * mutation built from it fails loudly rather than touching a real folder.
 */
export const UNASSIGNED_MEDIA_FOLDER_ID = 'unassigned-media' as CaseId;

export const isUnassignedFolderId = (id: string | null | undefined): boolean =>
  id === UNASSIGNED_MEDIA_FOLDER_ID;

/**
 * Is this row an unassigned file? The redact-state SDO's EXISTENCE is what marks
 * a file as unassigned (see the module header), so carrying an `sdoId` IS the
 * test — the folder tree cannot answer it.
 *
 * A type guard rather than a predicate because every caller that branches on it
 * then needs the id itself (SDO delete, the fileTdo payload), and `sdoId` is
 * declared optional on `AssetFileType` — narrowing here is what keeps those call
 * sites from re-deriving it.
 */
export const isUnassignedFile = <T extends { sdoId?: string }>(
  file: T | null | undefined
): file is T & { sdoId: string } => Boolean(file?.sdoId);

/**
 * Untranslated fallback name held on the synthetic folder. Selectors have no
 * intl context, so anything rendering this folder's name to the user formats the
 * `legacyMediasTitle` message instead (EN "Unassigned Media" / FR) and only
 * falls back to this.
 */
export const UNASSIGNED_MEDIA_FOLDER_NAME = 'Unassigned Media';

/**
 * SDO record + its enriched TDO → the `AssetFileType` the file pipeline expects.
 *
 * The single cast is deliberate. `AssetFileType` is a loose union of TDO and
 * Asset shapes: fields like `contentType`, `assetType` and `signedUri` are
 * declared required but belong to assets, and are equally absent at runtime on
 * the case-files path (`fetchCaseContentsQuery` doesn't select them on
 * `childTDOs` either). Filling them with invented values would make unassigned
 * files behave DIFFERENTLY from case files, which is the opposite of the point,
 * so we hand over exactly what the API returned.
 *
 * `status` is deliberately the TDO's OWN status ('recorded' / 'downloaded'),
 * exactly as it is for a request's files. It used to be overwritten with the
 * SDO's redaction label, which broke every consumer that reads it as a TDO
 * status: getFileProcessingStatus fell through to its `default: 'error'` branch
 * and painted a red badge on healthy files, and `isReady` never matched
 * 'recorded'. The redaction label belongs in `details.tags`, where case files
 * carry it — so if the TDO has no redaction tag (an old file whose status was
 * only ever written to its SDO), seed one from the SDO rather than clobbering
 * `status`.
 */
export const mapUnassignedFile = (
  legacy: LegacyMediaDetailType,
  tdo: UnassignedTdoRecord | undefined
): AssetFileType => {
  const details = (tdo?.details ?? legacy.details ?? {}) as Record<
    string,
    unknown
  >;
  const veritoneFile = (details.veritoneFile ?? {}) as {
    fileType?: string;
    mimetype?: string;
  };
  const tags = (details.tags ?? []) as ({ value?: string } | null)[];
  const hasRedactionTag = tags.some(
    (tag) => tag?.value === IN_REDACTION_TAG_KEY
  );
  return {
    ...(tdo ?? {}),
    id: legacy.tdoId,
    sdoId: legacy.sdoId,
    // Before its TDO detail lands, a row still needs a name; the SDO seeds
    // `tdoName` with the tdoId. Such rows are filtered out of the folder until
    // their detail arrives (see selectUnassignedFiles) precisely so that id is
    // never shown.
    name: tdo?.name ?? legacy.tdoName,
    details: hasRedactionTag
      ? details
      : {
          ...details,
          tags: [
            ...tags,
            {
              value: IN_REDACTION_TAG_KEY,
              redactionStatus: legacy.status,
              // getRedactionStatusIdentity prefers the KEY, and a custom status
              // is stored by key — omitting it resolved customs by display
              // label, which findCustomRedactionStatus does not match.
              ...(legacy.statusKey
                ? { redactionStatusKey: legacy.statusKey }
                : {}),
            },
          ],
        },
    // `veritoneFile` is declared required on AssetFileType but is only ever
    // present nested under `details` — mirror it up so the file-type helpers
    // (isPdfFile, isImageFile) read the same field they read for case files.
    veritoneFile: {
      fileType: veritoneFile.fileType ?? legacy.fileType ?? '',
      mimetype: veritoneFile.mimetype,
    },
  } as AssetFileType;
};

/**
 * The reverse of `mapUnassignedFile`, for the drag-to-file path.
 *
 * Dropping onto a request dispatches `actionFileTdoWithCaseRequest`, whose
 * payload is SDO-shaped (it has to delete the redact-state SDO as well as file
 * the TDO). Rebuilding that shape here means the drop handler and its
 * `isLegacyMediaDetail` guard stay exactly as they were, whether the drag
 * started in the docked panel or in the full-width file list.
 *
 * Returns null when the file isn't unassigned (no `sdoId`) — nothing else is
 * draggable out of this folder.
 */
export const unassignedDragSource = (
  file: AssetFileType
): LegacyMediaDetailType | null => {
  if (!isUnassignedFile(file)) {
    return null;
  }
  return {
    sdoId: file.sdoId,
    tdoId: file.id,
    tdoName: file.name,
    // The TDO's current parent, which the move must unfile it from. Comes from
    // the enriched query's `folders` selection.
    parentFolderId: file.folders?.[0]?.id as unknown as CaseId,
    signedUri: file.primaryAsset?.signedUri ?? '',
    fileType: file.veritoneFile?.fileType ?? '',
    status: file.status as LegacyMediaDetailType['status'],
    jobs: null,
    createdDateTime: file.createdDateTime,
    modifiedDateTime: file.modifiedDateTime,
  };
};

/**
 * The synthetic folder. `contentTemplates` is left EMPTY on purpose — a fake
 * content template would flow into the case delete/rename paths, which
 * dereference `contentTemplates[0].sdoId`. Callers that need "does this behave
 * like a request?" test `isUnassignedFolderId` alongside `contentTemplates`
 * instead.
 */
export const buildUnassignedFolder = ({
  files,
  outputFiles,
  name,
  fetchMoreAvailable,
  fetchingFiles,
}: {
  files: AssetFileType[];
  // The files' redacted/audit-log/transcript assets, flattened. Download All
  // and the multi-select download both read outputs off the folder rather than
  // off each file, so leaving this empty made them report "No output files to
  // export" no matter what the folder held.
  outputFiles: AssetFileType[];
  name: string;
  fetchMoreAvailable: boolean;
  fetchingFiles: boolean;
}): FolderStateType<CaseId> => ({
  id: UNASSIGNED_MEDIA_FOLDER_ID,
  name,
  contentTemplates: [],
  caseFiles: { inputFiles: files, outputFiles },
  count: files.length,
  fetchMoreAvailable,
  fetchingSubFolders: fetchingFiles,
  subfolders: [],
  folderPath: [],
});
